import Icon from "@Components/generic/icon";
import PlayPositions from "@Enums/PlayPositions";
import PlayPositionsShortnameMap from "@Enums/PlayPositionsShortnameMap";
import { faGear, faGripLines, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Box, Dialog, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { PlaylistScenario } from "@Shared/schemas/playlists/playlistSchemas";
import { ScenarioConfig } from "@Shared/schemas/scenarios/scenarioSchemas";
import { Reorder, useDragControls } from "framer-motion";
import { useState } from "react";
import ScenarioSettings from "./playlist-scenario-settings";

interface IPlaylistScenarioListItem {
  value: PlaylistScenario;
  config?: Partial<ScenarioConfig>;
  onConfigChange?: (config: Partial<ScenarioConfig>) => void;
  onDelete?: () => void;
  isNewAddition?: boolean;
}

const PlaylistScenarioListItem = ({ value, config, onConfigChange, onDelete, isNewAddition }: IPlaylistScenarioListItem) => {
  const theme = useTheme();
  const controls = useDragControls();
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  return (
    <>
      <Reorder.Item
        value={value}
        dragListener={false}
        dragControls={controls}
        style={{
          backgroundColor: theme.palette.dsBase.v100,
          borderRadius: theme.spacing(1),
          marginBottom: theme.spacing(1),
          border: isNewAddition ? '2px dashed' : '1px solid',
          borderColor: isNewAddition ? theme.palette.dsNeutral.v100 : theme.palette.dsSurface.v300,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            padding: theme.spacing(2),
            "&:hover": {
              backgroundColor: theme.palette.dsSurface.v200,
            },
          }}
        >
          <Stack
            spacing={0.5}
            sx={{ cursor: "grab" }}
            onPointerDown={(e) => controls.start(e)}
          >
            <Icon icon={faGripLines} color={theme.palette.dsNeutral.v400} />
          </Stack>
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography variant="textLg700" color="dsBase.v200">
              {value.scenario.name}
            </Typography>
            <Typography variant="textBase500" color="dsNeutral.v400">
              {[
                value.scenario.phase_of_play,
                value.scenario.play_positions?.length 
                  ? value.scenario.play_positions.map((p: PlayPositions) => PlayPositionsShortnameMap[p]).join(", ")
                  : null
              ].filter(Boolean).join(" · ")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => setConfigDialogOpen(true)}>
              <Icon icon={faGear} color={theme.palette.dsNeutral.v400} />
            </IconButton>
            <IconButton onClick={onDelete}>
              <Icon icon={faTrash} color={theme.palette.dsNeutral.v400} />
            </IconButton>
          </Stack>
        </Stack>
      </Reorder.Item>

      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <ScenarioSettings
            title={`Settings for ${value.scenario.name}`}
            config={config || {}}
            onChange={(newConfig) => {
              if (onConfigChange) {
                onConfigChange(newConfig);
              }
            }}
          />
        </Box>
      </Dialog>
    </>
  );
};

export default PlaylistScenarioListItem;
